import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { DASHBOARD_SIGNIN_URL, DASHBOARD_SIGNUP_URL } from "@/config/urls";

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    // Intersection observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll(".scroll-reveal");
    elements.forEach((el) => observer.observe(el));

    return () => {
      clearTimeout(timer);
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <section 
      ref={heroRef}
      className="ophira-texture relative min-h-[80vh] flex items-center overflow-hidden bg-ophira-dark/95"
    >
      {/* Background elements */}
      
      <div className="container mx-auto px-4 flex flex-col lg:flex-row items-center gap-6 lg:gap-12 z-10 mt-0 pt-16 lg:mt-[-60px]">
        <div className={`w-full lg:w-1/2 space-y-6 ${isVisible ? 'animate-slide-in-left' : 'opacity-0'}`} style={{ animationDelay: "0.2s" }}>
          <h1 className="text-3xl md:text-4xl lg:text-7xl font-bold leading-tight mb-2">
            Distribute Your <span className="text-ophira-yellow relative">Music</span> To The World
          </h1>
          <p className="text-white/65 text-lg mb-8 max-w-2xl">
            Reach millions of listeners across all major streaming platforms. Upload once, distribute everywhere, and keep 100% ownership.
          </p>
          <div className="flex flex-wrap gap-4">
            <a href={DASHBOARD_SIGNIN_URL}>
              <Button className="ophira-button text-base px-8 py-6 group">
                Start Distributing
                <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
              </Button>
            </a>
            <Link to="/services">
              <Button variant="outline" className="text-base px-8 py-6 bg-black/20 border-ophira-yellow/50 text-white hover:text-white hover:bg-ophira-yellow/10 group">
                Explore Music Distribution Services 
                <ArrowRight size={16} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
          <div className="text-xs text-white/65 mt-3">
            By signing up, you agree to our <Link to="/terms-and-conditions" className="text-ophira-yellow hover:underline">Terms of Service</Link>
          </div>
        </div>
        
        <div className={`w-full lg:w-1/2 ${isVisible ? 'animate-slide-in-right' : 'opacity-0'} mt-8 lg:mt-0`} style={{ animationDelay: "0.1s" }}>
          {/* Desktop version - only visible on large screens */}
          <div className="relative mx-auto max-w-md lg:max-w-full hidden lg:block">
            {/* Main Album Image */}
            <div className="overflow-hidden relative bg-ophira-darkgray/30 p-2 rounded-md shadow-xl">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/uploads/studioimag23.webp?w=400 400w, /uploads/studioimag23.webp?w=800 800w"
                  sizes="(max-width: 768px) 400px, 800px"
                />
                <img
                  src="/uploads/studioimag23.webp"
                  alt="Music studio workstation with YouAux distribution dashboard"
                  className="w-full h-auto object-cover"
                  width={800}
                  height={600}
                  loading="eager"
                  decoding="async"
                />
              </picture>
            </div>
            
            {/* Defer loading of non-critical images */}
            <div className="absolute -top-6 -right-6 w-40 h-32 p-3 rotate-12 bg-ophira-darkgray/90 shadow-lg animate-float hover:scale-105 transition-transform" style={{ animationDelay: "1s" }}>
              <img
                src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=400&auto=format&fit=crop"
                alt="Studio headphones resting on a mixer"
                className="w-full h-auto rounded-lg"
                loading="lazy"
                width={400}
                height={300}
              />
            </div>
            
            <div className="absolute -bottom-8 -left-6 w-40 h-32 p-3 -rotate-6 bg-ophira-darkgray/90 shadow-lg animate-float hover:scale-105 transition-transform" style={{ animationDelay: "1s" }}>
              <img
                src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="Independent artist recording vocals in studio"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>

          {/* Mobile version - only visible on small screens */}
          <div className="relative lg:hidden">
            {/* Main Album Image for mobile */}
            <div className="overflow-hidden relative group bg-ophira-darkgray/30 p-2 rounded-md shadow-xl">
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <picture>
                <source
                  type="image/webp"
                  srcSet="/uploads/studioimag23.webp?w=400 400w, /uploads/studioimag23.webp?w=800 800w, /uploads/studioimag23.webp?w=1200 1200w"
                  sizes="(max-width: 768px) 100vw, 800px"
                />
                <img
                  src="/uploads/studioimag23.webp"
                  alt="Music studio workstation shown on mobile screen"
                  className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </picture>
            </div>
            
            {/* Floating small cards for mobile */}
            <div className="absolute -top-4 right-0 w-48 h-36 p-2 rotate-12 bg-ophira-darkgray/90 shadow-lg animate-float hover:scale-105 transition-transform" style={{ animationDelay: "1s" }}>
              <img
                src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="Studio headphones resting on a mixer"
                className="w-full h-auto rounded-lg"
              />
            </div>
            
            <div className="absolute -bottom-4 -left-4 w-48 h-36 p-2 -rotate-6 bg-ophira-darkgray/90 shadow-lg animate-float hover:scale-105 transition-transform" style={{ animationDelay: "1s" }}>
              <img
                src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="Independent artist recording vocals in studio"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Scrolling Text Banner - improved for mobile */}
      <div className="absolute -bottom-0 left-0 w-full overflow-hidden py-2 md:py-4 bg-ophira-darkgray/80 backdrop-blur-sm border-t border-ophira-yellow/10">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(5)].map((_, i) => (
            <span key={i} className="text-base md:text-2xl font-bold mx-4 text-ophira-lightgray">
              • DISTRIBUTE YOUR MUSIC • KEEP 100% OWNERSHIP • GLOBAL REACH • ADVANCED ANALYTICS • PLAYLIST PROMOTION •
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
